/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { ofType } from 'redux-observable';
import { SecurityHelper as SecurityUtils } from 'sarsaparilla';
import { writeSession } from 'shared-ui/auth/session';
import axios from 'axios';
import { from, of, merge, combineLatest } from 'rxjs';
import {
    map,
    debounceTime,
    withLatestFrom,
    pluck,
    mergeMap,
    reduce,
    catchError,
} from 'rxjs/operators';
import find from 'lodash/find';
import lodashMap from 'lodash/map';
import lodashGet from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import { UPDATE_EMAIL_SENT, ACCOUNT_UPDATED } from 'ui-account/src/constants';
import { updateFailed } from 'ui-account/src/actions/profileActions';
import {
    RENTAL_BASEPATH,
    SET_VALIDATION_ERRORS,
    CONFIRM_PASS,
    PROCESS_ORDER_MODIFICATION,
    OPEN_PASS_CONFIRMATION_MODAL,
    COMPLETE_SUBMISSION_FAILURE,
    COMPLETE_SUBMISSION_SUCCESS,
} from '../../shared/constants/index';
import { GOLDEN_ACCESS_PASS, GOLDEN_AGE_PASS } from '../../shared/constants/passes';
import { PASS_TYPE_PATH, PASS_NUMBER_PATH } from '../../shared/constants/orderDetails';
import { authCampingAxios } from '../../shared/utils/campingAxios';
import { updateNightMapOccupants } from '../../shared/utils/reservation_utils';

// order details update axios request for CAMPSITE reservations
export function updateReservation(reservation) {
    const newNightMap = updateNightMapOccupants(reservation);
    return authCampingAxios.put(`/reservations/${reservation.reservation_id}/modify`, {
        campsite_id: reservation.campsite_id,
        check_in: reservation.start_date,
        check_out: reservation.end_date,
        site_occupant: reservation.site_occupant,
        reservation_options: {
            ...reservation.reservation_options,
            night_map: isEmpty(newNightMap) ? undefined : newNightMap,
        },
    });
}

// order details update axios request for RENTAL reservations
export const updateRentalReservation = (reservation) => {
    return axios.put(
        `${process.env.API}${RENTAL_BASEPATH}/reservations/${reservation.reservation_id}`,
        {
            campsite_id: reservation.campsite_id,
            check_in: reservation.start_date,
            check_out: reservation.end_date,
            resource_id: reservation.resource_id,
            quantity: reservation.quantity,
            site_occupant: reservation.site_occupant,
            reservation_options: reservation.reservation_options,
        },
        { headers: { Authorization: SecurityUtils.getAuthHeader() } }
    );
};

// order details update axios request for reserved CAMPSITE reservations
export function updateReservedReservation(reservation) {
    const newNightMap = updateNightMapOccupants(reservation);
    return authCampingAxios.put(`/reservations/${reservation.reservation_id}/modify`, {
        reservation_id: reservation.reservation_id,
        campsite_id: reservation.campsite_id,
        check_in: reservation.start_date,
        check_out: reservation.end_date,
        site_occupant: reservation.site_occupant,
        reservation_options: {
            ...reservation.reservation_options,
            night_map: isEmpty(newNightMap) ? undefined : newNightMap,
        },
    });
}

// order details update axios request for reserved CAMPSITE reservations
export function updateReservedRentalReservation(reservation) {
    // Set the required reservation if available in the reservation vars
    const reservationOptions = lodashGet(reservation, 'reservation_options', {});
    const requiredReservation = lodashGet(
        reservation,
        'vars.overnight_reservation',
        null
    );
    if (!isNil(requiredReservation)) {
        reservationOptions.required_reservation = requiredReservation;
    }

    return axios.put(
        `${process.env.API}${RENTAL_BASEPATH}/reservations/${reservation.reservation_id}/modify`,
        {
            reservation_id: reservation.reservation_id,
            check_in: reservation.start_date,
            check_out: reservation.end_date,
            resource_id: reservation.resource_id,
            quantity: reservation.quantity,
            site_occupant: reservation.site_occupant,
            reservation_options: reservationOptions,
        },
        { headers: { Authorization: SecurityUtils.getAuthHeader() } }
    );
}

export function updateAccountInfo(newProfile) {
    return axios.put(`${process.env.API}/accounts/account`, newProfile, {
        headers: { Authorization: SecurityUtils.getAuthHeader() },
    });
}

/**
 * check all pending reservations and return true if any of the reservations contains 'American the Beauty' pass
 * @param {object} pendingReservations - list of pending reservations
 */
function hasAmericanTheBeautyPass(pendingReservations) {
    const reservationWithPass = find(pendingReservations, (reservation) => {
        const passType = lodashGet(reservation, PASS_TYPE_PATH, '');
        const passNumber = lodashGet(reservation, PASS_NUMBER_PATH, '');

        // Check if third number in the Pass ID is a '1' (a.k.a America the Beautiful pass)
        return (
            (passType === GOLDEN_ACCESS_PASS || passType === GOLDEN_AGE_PASS) &&
            passNumber.charAt(2) === '1'
        );
    });

    return !isNil(reservationWithPass);
}

/**
 *
 * @param {Observable} action$ - a stream of actions
 * @param {Observable} state$ - a stream of redux store states
 * @param {object} dependencies - redux-observable epic dependencies
 * @param {import('@reduxjs/toolkit').Dispatch} dependencies.dispatch - store
 *   dispatch, used to route account-session writes through `writeSession`
 */
export const submitOrderDetailsEpic = (action$, state$, { dispatch }) =>
    action$.pipe(
        ofType(
            SET_VALIDATION_ERRORS, // attempt submission when validation errors are being updated
            CONFIRM_PASS, // attempt submission when user confirms their pass (when applicable)
            PROCESS_ORDER_MODIFICATION // when submitting a direct update from account modification page
        ),
        debounceTime(500), // debounce for half second because SET_VALIDATION_ERRORS could be called multiple times
        withLatestFrom(
            combineLatest(
                state$.pipe(pluck('orderDetails')),
                state$.pipe(pluck('auth'), pluck('account'))
            )
        ),
        mergeMap((stream) => {
            const states = stream[1];

            const reservationState = states[0];
            const accountState = states[1];

            const validationErrors = reservationState.pendingReservationsErrors;
            const {
                reservations,
                pendingReservations,
                accountToUpdate,
                isPassConfirmed,
            } = reservationState;

            const reservationStatus = lodashGet(
                reservations,
                '[0].reservation_status',
                'RESERVED'
            );

            // if there are no validation errors, attempt submissions
            if (isEmpty(validationErrors)) {
                /* ROAD BLOCK: if the pending reservations include pass that needs to be confirmed
            and has not been confirmed yet, return OPEN_PASS_CONFIRMATION_MODAL to triggering
            showing the confirmation modal */
                const shouldConfirmPass =
                    hasAmericanTheBeautyPass(pendingReservations) && !isPassConfirmed;
                if (shouldConfirmPass) {
                    return of({
                        type: OPEN_PASS_CONFIRMATION_MODAL,
                    });
                }

                /* CONTINUE SUBMISSION */
                // submission stream per reservation submission request
                const submissionStream = (reservation) => {
                    let updateRequest;
                    // if reservationStatus is RESERVED...
                    if (reservationStatus === 'RESERVED') {
                        updateRequest = reservation.resource_id
                            ? updateReservedRentalReservation(reservation)
                            : updateReservedReservation(reservation);
                    }
                    // if reservationStatus is HOLD...
                    else {
                        updateRequest = reservation.resource_id
                            ? updateRentalReservation(reservation)
                            : updateReservation(reservation);
                    }

                    return from(updateRequest).pipe(
                        catchError((error) =>
                            of({
                                reservation,
                                error: error.response,
                            })
                        )
                    );
                };

                // save account stream
                const saveAccountStream = () => {
                    const account = lodashGet(accountState, 'account', null);

                    // if accountToUpdate is not empty
                    if (account && !isEmpty(accountToUpdate)) {
                        // find the reservation that we're going to update the user's account with
                        const reservationToUpdateWith =
                            pendingReservations[accountToUpdate];

                        // set the pending user account info with the primary occupant info filled in the pending reservation
                        const primaryOccupant = lodashGet(
                            reservationToUpdateWith,
                            'site_occupant.primary_occupant',
                            {}
                        );
                        const newProfile = {
                            ...account,
                            first_name: primaryOccupant.first_name,
                            last_name: primaryOccupant.last_name,
                            home_phone: primaryOccupant.phone,
                            cell_phone: primaryOccupant.phone,
                            home_address: {
                                ...account.home_address,
                                zip_code: primaryOccupant.zip,
                            },
                        };

                        return from(updateAccountInfo(newProfile)).pipe(
                            map((response) => {
                                const profile = response.data;
                                if ('success' in response.data) {
                                    return {
                                        type: UPDATE_EMAIL_SENT,
                                    };
                                }
                                // Route through the shared helper so the session
                                // is both persisted to localStorage (source of
                                // truth) and projected into Redux, keeping
                                // state.auth.account in sync with the update.
                                writeSession(dispatch, profile);
                                return {
                                    type: ACCOUNT_UPDATED,
                                };
                            }),
                            // catchError's selector must return a stream; a plain
                            // object throws "invalid object where a stream was
                            // expected" and tears down the merged submission
                            // stream, hanging checkout.
                            //
                            // Build the action with `updateFailed` rather than by
                            // hand: ui-account's profileReducer reads
                            // `action.payload.err` unguarded, so a payload-less
                            // ACCOUNT_UPDATE_FAILED throws inside `store.dispatch`,
                            // which RxJS treats as a failed `next` handler and
                            // unsubscribes the entire root epic.
                            catchError((error) =>
                                of(
                                    updateFailed(
                                        error?.response?.data?.error ||
                                            error?.message ||
                                            'Unknown error'
                                    )
                                )
                            )
                        );
                    }
                    /* if accountToUpdate is empty (users didn't choose to save account info),
                return empty array to skip this stream */
                    return [];
                };

                // For each pending reservations...
                const allRes = lodashMap(pendingReservations, (r) => r);
                return merge(
                    saveAccountStream(),
                    of(...allRes).pipe(
                        mergeMap(submissionStream),
                        reduce((acc, val) => {
                            // reduces the responses from the multi submissionStreams into a single array
                            return [...acc, val];
                        }, []),
                        map((allResponses) => {
                            // when all the submission requests have completed...
                            // check if any request resulted in errors
                            const errorResponse = find(
                                allResponses,
                                (response) => !isNil(response.error)
                            );

                            // if there are error responses, return COMPLETE_SUBMISSION_FAILURE (show error)
                            if (errorResponse) {
                                return {
                                    type: COMPLETE_SUBMISSION_FAILURE,
                                    reservation: errorResponse.reservation,
                                    error: errorResponse.error.data.error,
                                };
                            }
                            // if all responses are successful, return COMPLETE_SUBMISSION_SUCCESS (redirect to cart)
                            return {
                                type: COMPLETE_SUBMISSION_SUCCESS,
                            };
                        })
                    )
                );
            }
            // there are validation error, return COMPLETE_SUBMISSION_FAILURE (show validation error)
            return of({
                type: COMPLETE_SUBMISSION_FAILURE,
            });
        })
    );
